import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { addDays, format } from "date-fns";
import { Check, MapPin, Clock, User } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { money } from "../../lib/helpers";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Field } from "../../components/Common";
import { BookingPay } from "../../components/BookingPay";
import PublicFrame, { usePublicBranding } from "./PublicFrame";
import { ServicePicker } from "./ServicePicker";
import { ProfileFields, PROFILE_BLANK, missingProfile } from "../../components/ProfileFields";

const STEPS = ["choose_location", "choose_service", "choose_pro", "choose_time", "your_details"];

function Choice({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`lift w-full text-left rounded-[var(--radius)] border bg-card p-5 flex items-center gap-4 ${active ? "ring-2 ring-primary border-primary" : ""}`}>
      <div className="flex-1 min-w-0">{children}</div>
      {active && <Check className="h-5 w-5 text-primary" />}
    </button>
  );
}

export default function Book() {
  const { t, lang } = useT();
  const nav = useNavigate();
  const { slug } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const [data, setData] = useState(null);
  const [step, setStep] = useState(0);
  const [sel, setSel] = useState({ location_id: "", service_id: "", professional_id: "any", date: format(new Date(), "yyyy-MM-dd"), time: "" });
  const [slots, setSlots] = useState([]);
  const [d, setD] = useState({ first_name: "", last_name: "", email: "", phone: "", ...PROFILE_BLANK, notes: "", whatsapp_opt_in: false });
  const [pay, setPay] = useState({ code: params.get("code") || "", gift_code: params.get("gift") || "", pay_full: false });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { api.get(`/public/c/${slug}/clinic`).then((r) => setData(r.data)).catch(() => setData(false)); }, [slug]);
  usePublicBranding(data);

  useEffect(() => {
    if (step !== 3) return;
    setSlots(null);
    api.get(`/public/c/${slug}/slots`, { params: { location_id: sel.location_id, service_id: sel.service_id, professional_id: sel.professional_id, date: sel.date } })
      .then((r) => setSlots(r.data)).catch(() => setSlots([]));
  }, [step, sel.date, sel.location_id, sel.service_id, sel.professional_id, slug]);

  useEffect(() => {
    if (!data) return;
    const l = params.get("location");
    const s = params.get("service");
    if (!l || !data.locations.some((x) => x.id === l)) return;
    const ok = s && data.services.some((x) => x.id === s && x.prices?.[l]);
    setSel((p) => ({ ...p, location_id: l, service_id: ok ? s : "" }));
    setStep(ok ? 2 : 1);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (data === false) return <PublicFrame><p data-testid="book-not-found">Clinic not found.</p></PublicFrame>;

  if (!data) return <PublicFrame><p className="text-muted-foreground">…</p></PublicFrame>;
  const loc = data.locations.find((l) => l.id === sel.location_id);
  const svcs = data.services.filter((s) => s.prices?.[sel.location_id]);
  const svc = data.services.find((s) => s.id === sel.service_id);
  const pros = data.professionals.filter((p) => p.location_ids.includes(sel.location_id) && (!svc?.professional_ids?.length || svc.professional_ids.includes(p.id)));
  const price = svc && loc ? svc.prices[loc.id] : null;
  const pick = (k, v, next) => { setSel({ ...sel, [k]: v, ...(k === "location_id" ? { service_id: "", professional_id: "any" } : {}), time: "" }); if (next) setStep(next); };
  const days = Array.from({ length: 14 }).map((_, i) => format(addDays(new Date(), i), "yyyy-MM-dd"));

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data: r } = await api.post(`/public/c/${slug}/book`, { ...sel, ...d, ...pay, language: lang, origin_url: window.location.origin });
      sessionStorage.setItem("mcm_manage", r.manage_token);
      sessionStorage.setItem("mcm_slug", slug);
      sessionStorage.setItem("mcm_kind", "booking");
      if (r.checkout_url && embed) { window.open(r.checkout_url, "_blank"); setBusy(false); }
      else if (r.checkout_url) window.location.href = r.checkout_url;
      else nav(`/manage/${r.manage_token}?new=1`);
    } catch (e) { setError(errMsg(e)); setBusy(false); }
  };

  return (
    <PublicFrame branding={data.branding} embed={embed}>
      <h1 className="text-3xl sm:text-4xl font-bold mb-2">{t("book_title")}</h1>
      <div className="flex gap-1.5 mb-8 mt-4" data-testid="booking-steps">
        {STEPS.map((s, i) => <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-muted"}`} />)}
      </div>
      <h2 className="text-lg font-semibold mb-4" data-testid="booking-step-title">{t(STEPS[step])}</h2>

      {step === 0 && <div className="grid gap-3">{data.locations.map((l) => (
        <Choice key={l.id} testId={`book-location-${l.id}`} active={sel.location_id === l.id} onClick={() => pick("location_id", l.id, 1)}>
          <div className="font-semibold flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" />{l.name}</div>
          <div className="text-sm text-muted-foreground">{l.address} · {l.currency}</div>
        </Choice>))}</div>}

      {step === 1 && <ServicePicker svcs={svcs} selected={sel.service_id} onPick={(id) => pick("service_id", id, 2)} loc={loc} lang={lang} t={t} />}

      {step === 2 && <div className="grid gap-3">
        <Choice testId="book-pro-any" active={sel.professional_id === "any"} onClick={() => pick("professional_id", "any", 3)}><div className="font-semibold">{t("any_pro")}</div></Choice>
        {pros.map((p) => (
          <Choice key={p.id} testId={`book-pro-${p.id}`} active={sel.professional_id === p.id} onClick={() => pick("professional_id", p.id, 3)}>
            <div className="font-semibold flex items-center gap-2"><User className="h-4 w-4" style={{ color: p.color }} />{p.name}</div>
            {p.title && <div className="text-sm text-muted-foreground">{p.title}</div>}
          </Choice>))}</div>}

      {step === 3 && (
        <div className="space-y-5">
          <div className="flex gap-2 overflow-x-auto pb-2" data-testid="book-days">
            {days.map((day) => (
              <button key={day} data-testid={`book-day-${day}`} onClick={() => setSel({ ...sel, date: day, time: "" })}
                className={`shrink-0 w-16 rounded-[var(--radius)] border py-2 text-center ${sel.date === day ? "bg-primary text-primary-foreground border-primary" : "bg-card"}`}>
                <div className="text-[11px] uppercase">{format(new Date(day), "EEE")}</div><div className="font-display text-lg font-bold">{format(new Date(day), "d")}</div>
              </button>
            ))}
          </div>
          {slots === null ? <p className="text-sm text-muted-foreground">…</p> : !slots.length ? <p className="text-sm text-muted-foreground" data-testid="book-no-slots">{t("no_slots")} <a className="text-primary underline" data-testid="book-join-waitlist" href={`/waitlist/${slug}?service=${sel.service_id}&location=${sel.location_id}&date=${sel.date || ""}`}>Join the waitlist</a></p> : (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="book-slots">
              {slots.map((s) => (
                <button key={s.time} data-testid={`book-slot-${s.time.replace(":", "")}`} onClick={() => { setSel({ ...sel, time: s.time }); setStep(4); }}
                  className={`rounded-full border py-2 text-sm font-medium hover:border-primary ${sel.time === s.time ? "bg-primary text-primary-foreground" : "bg-card"}`}>{s.time}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-5">
          <div className="rounded-[var(--radius)] border bg-muted/40 p-4 text-sm" data-testid="book-summary">
            <b>{svc?.name}</b> · {loc?.name} · {sel.date} {sel.time}<br />
            {t("price")}: {money(price?.price, loc?.currency, lang)}{price?.deposit > 0 && <> · <b>{t("deposit")}: {money(price.deposit, loc.currency, lang)}</b></>}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {["first_name", "last_name", "email", "phone"].map((k) => (
              <Field key={k} label={t(k)}><Input data-testid={`book-${k}-input`} type={k === "email" ? "email" : k === "phone" ? "tel" : "text"} value={d[k]} onChange={(e) => setD({ ...d, [k]: e.target.value })} /></Field>
            ))}
            <ProfileFields value={d} onChange={setD} t={t} testPrefix="book" required />
            <Field label={t("notes")} className="sm:col-span-2"><Textarea data-testid="book-notes-input" value={d.notes} onChange={(e) => setD({ ...d, notes: e.target.value })} /></Field>
          </div>
          <BookingPay slug={slug} base={{ location_id: sel.location_id, service_id: sel.service_id }} email={d.email} value={pay} onChange={setPay} onQuote={setQuote} lang={lang} />
          <label className="flex items-center gap-3 text-sm">
            <Checkbox data-testid="book-whatsapp-optin" checked={d.whatsapp_opt_in} onCheckedChange={(v) => setD({ ...d, whatsapp_opt_in: !!v })} />{t("whatsapp_opt_in_public")}
          </label>
          {error && <p className="text-sm text-destructive" data-testid="book-error">{error}</p>}
          <Button data-testid="book-submit-btn" className="w-full rounded-full h-12" disabled={busy || !quote || !d.first_name || !d.last_name || !d.email || !d.phone || missingProfile(d).length > 0} onClick={submit}>
            {quote?.charge_now > 0 ? `${quote.pay_now === quote.total ? "Pay now" : t("pay_deposit")} · ${money(quote.charge_now, quote.currency, lang)}` : t("confirm_booking")}
          </Button>
        </div>
      )}
      {step > 0 && <button className="mt-8 text-sm text-muted-foreground hover:text-foreground" data-testid="book-back-btn" onClick={() => setStep(step - 1)}>← {t("back")}</button>}
    </PublicFrame>
  );
}
